"use client";

import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/utils/supabase/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Users, Mail } from "lucide-react";
import { showSuccess, showError } from "@/utils/toast";
import { ClientKYCForm } from "@/components/ClientKYCForm";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Link } from "react-router-dom";
import { log_audit_action } from "@/lib/api-queries";
import { Button } from "@/components/ui/button";
import CompanyTypeBadge from "@/components/CompanyTypeBadge";
import { AddClientForm } from "@/components/AddClientForm";
import { InviteClientForm } from "@/components/InviteClientForm";

const Contacts = () => {
  const { profile, company, getFeatureAccess } = useAuth();
  const queryClient = useQueryClient();
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<"client" | "employee" | "manager">("client");
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [showKYC, setShowKYC] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [editingUserRole, setEditingUserRole] = useState<"client" | "employee" | "manager" | "admin">("client");

  // Only IBP Consulting companies require KYC
  const isConsultingCompany = getFeatureAccess('ibp_consulting');

  const { data: teamMembers } = useQuery({
    queryKey: ["team", company?.id],
    queryFn: async () => {
      if (!company?.id) return [];
      let query = supabase
        .from("profiles")
        .select("*")
        .eq("company_id", company.id)
        .order("created_at");

      if (!getFeatureAccess('enterprise')) {
        query = query.limit(getFeatureAccess('pro') ? 50 : 2);
      }

      const { data } = await query;
      return data || [];
    },
  });

  const { data: clients } = useQuery({
    queryKey: ["clients", company?.id],
    queryFn: async () => {
      if (!company?.id) return [];
      let query = supabase
        .from("clients")
        .select("*")
        .eq("company_id", company.id)
        .order("created_at", { ascending: false });

      // Clients are only managed by SaaS companies, not IBP consulting companies
      if (isConsultingCompany) return [];

      if (!getFeatureAccess('enterprise')) {
        query = query.limit(getFeatureAccess('pro') ? 500 : 5);
      }

      const { data } = await query;
      return data || [];
    },
  });

  const canAddMoreAgents = () => {
    if (getFeatureAccess('enterprise')) return true;
    return (teamMembers?.length || 0) < (getFeatureAccess('pro') ? 50 : 2);
  };

  const canAddMoreClients = () => {
    if (getFeatureAccess('enterprise')) return true;
    return (clients?.length || 0) < (getFeatureAccess('pro') ? 500 : 5);
  };

  const inviteMutation = useMutation({
    mutationFn: async (email: string) => {
      if (!company?.id) throw new Error('No company ID for invite');

      const { data, error } = await supabase.functions.invoke('create-invite', {
        body: {
          email,
          company_id: company.id,
          role: inviteRole,
        },
      });

      if (error) throw new Error((error as any).message || 'Failed to create invite');
      return data; // { success: true, invite_id, message }
    },
    onSuccess: (result, email) => {
      showSuccess(`Invite sent to ${email}! They can join via the link in their email.`);
      setInviteEmail("");
      log_audit_action(profile?.id || 'system', 'invite_user', 'profile', { inviteEmail: email, role: inviteRole, invite_id: (result as any)?.invite_id });
    },
    onError: (error, email) => {
      console.error('Invite mutation error:', error);
      showError("Failed to send invite: " + (error as any).message);
      log_audit_action(profile?.id || 'system', 'invite_failed', 'profile', { inviteEmail: email, role: inviteRole, error: (error as any).message });
    },
  });

  const resendInviteMutation = useMutation({
    mutationFn: async (email: string) => {
      if (!company?.id) throw new Error('No company ID for invite');
      const { data, error } = await supabase.functions.invoke('create-invite', {
        body: {
          email,
          company_id: company.id,
          role: 'client',
        },
      });
      if (error) throw new Error((error as any).message || 'Failed to resend invite');
      return data;
    },
    onSuccess: (_result, email) => {
      showSuccess("Invite resent successfully!");
      log_audit_action(profile?.id || 'system', 'resend_invite', 'profile', { inviteEmail: email });
    },
    onError: (error, email) => {
      console.error('Resend invite error:', error);
      showError("Failed to resend invite: " + (error as any).message);
      log_audit_action(profile?.id || 'system', 'resend_invite_failed', 'profile', { inviteEmail: email, error: (error as any).message });
    },
  });

  const updateRoleMutation = useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: "client" | "employee" | "manager" | "admin" }) => {
      const { data, error } = await supabase.rpc('update_user_role', {
        p_target_user_id: userId,
        p_new_role: role,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      showSuccess("User role updated successfully!");
      log_audit_action(profile?.id || 'system', 'update_user_role', 'profile', { target_user_id: editingUserId, new_role: editingUserRole });
      queryClient.invalidateQueries({ queryKey: ["team"] });
      setShowEditDialog(false);
      setEditingUserId(null);
    },
    onError: (error: any) => {
      console.error('Role update failed:', error);
      showError("Failed to update role: " + error.message);
      log_audit_action(profile?.id || 'system', 'failed_role_update', 'profile', { target_user_id: editingUserId, attempted_role: editingUserRole, error: error.message });
    },
  });

  const handleInvite = () => {
    if (inviteEmail && canAddMoreAgents()) {
      inviteMutation.mutate(inviteEmail);
    } else if (!canAddMoreAgents()) {
      showError('Upgrade to add more agents.');
      log_audit_action(profile?.id || 'system', 'invite_failed_limit', 'profile', { inviteEmail });
    }
  };

  const handleResendInvite = (email: string) => {
    resendInviteMutation.mutate(email);
  };

  const handleEditRole = (userId: string) => {
    setEditingUserId(userId);
    setShowEditDialog(true);
  };

  const handleUpdateRole = () => {
    if (editingUserId) {
      if (editingUserRole === 'admin' && profile?.role !== 'superadmin') {
        showError("Only superadmins can assign 'admin' roles.");
        log_audit_action(profile?.id || 'system', 'unauthorized_role_attempt', 'profile', { target_user_id: editingUserId, attempted_role: 'admin' });
        return;
      }
      updateRoleMutation.mutate({ userId: editingUserId, role: editingUserRole });
    }
  };

  const handleKYCComplete = () => {
    setShowKYC(false);
    queryClient.invalidateQueries({ queryKey: ["clients"] });
  };

  return (
    <div className="container mx-auto p-4">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Team & Contacts</h1>
          <p className="text-muted-foreground">Manage your company team members and clients.</p>
        </div>
        <CompanyTypeBadge />
      </div>

      {!canAddMoreAgents() && !getFeatureAccess('enterprise') && (
        <Alert className="mb-6">
          <AlertDescription>
            You've reached the agent limit for your plan ({getFeatureAccess('pro') ? 50 : 2} agents). <Link to="/dashboard/billing" className="text-primary font-medium">Upgrade to Enterprise</Link> for unlimited agents!
          </AlertDescription>
        </Alert>
      )}

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Invite New Team Member</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              placeholder="Email address"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              disabled={!canAddMoreAgents()}
            />
            <Select value={inviteRole} onValueChange={(value) => setInviteRole(value as "client" | "employee" | "manager")}>
              <SelectTrigger>
                <SelectValue placeholder="Role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="client">Client</SelectItem>
                <SelectItem value="employee">Employee (Basic access)</SelectItem>
                <SelectItem value="manager">Manager (View billing, invite employees)</SelectItem>
                {profile?.role === 'superadmin' && <SelectItem value="admin">Admin (Full access, including billing) - Restricted</SelectItem>}
              </SelectContent>
            </Select>
            <Button onClick={handleInvite} disabled={!inviteEmail || !canAddMoreAgents() || inviteMutation.isPending}>
              <Mail className="mr-2 h-4 w-4" />
              Send Invite {!canAddMoreAgents() && '(Upgrade for more agents)'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {isConsultingCompany && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>KYC for Consulting Clients</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <p className="text-sm text-muted-foreground">For IBP Consulting clients: Complete KYC and incorporation details.</p>
            <Button onClick={() => setShowKYC(true)} className="mt-2">Start KYC Process</Button>
          </CardContent>
          {showKYC && <ClientKYCForm isWightWayUser={true} onComplete={handleKYCComplete} />}
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {teamMembers?.map((member: any) => (
          <Card key={member.id}>
            <CardHeader className="flex flex-row items-center space-x-4 pb-2">
              <div className="flex items-center space-x-2">
                <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                  {member.first_name?.[0]}{member.last_name?.[0]}
                </div>
                <div>
                  <h3 className="font-medium">{member.first_name} {member.last_name}</h3>
                  <p className="text-sm text-muted-foreground">{member.email}</p>
                </div>
              </div>
              <Badge variant={member.role === 'admin' ? "default" : "secondary"}>{member.role}</Badge>
            </CardHeader>
            <CardContent>
              <Button variant="outline" size="sm" className="w-full" onClick={() => handleEditRole(member.id)}>
                Edit Role
              </Button>
              <Button variant="outline" size="sm" className="w-full mt-2" onClick={() => handleResendInvite(member.email)}>
                Resend Invite
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>
      {teamMembers?.length === 0 && (
        <Card>
          <CardContent className="text-center py-8">
            <Users className="h-12 w-12 text-muted-foreground mx-auto mb-2" />
            <p className="text-muted-foreground">No team members yet. Invite someone to get started!</p>
          </CardContent>
        </Card>
      )}

      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit User Role</DialogTitle>
            <DialogDescription>Select a new role for the user. Note: Only superadmins can assign 'admin' roles.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="role">New Role</Label>
              <Select value={editingUserRole} onValueChange={(value) => setEditingUserRole(value as "client" | "employee" | "manager" | "admin")}>
                <SelectTrigger>
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="client">Client</SelectItem>
                  <SelectItem value="employee">Employee (Basic access)</SelectItem>
                  <SelectItem value="manager">Manager (View billing, invite employees)</SelectItem>
                  {profile?.role === 'superadmin' && <SelectItem value="admin">Admin (Full access, including billing) - Restricted</SelectItem>}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateRole} disabled={updateRoleMutation.isPending}>
              {updateRoleMutation.isPending ? "Updating..." : "Update Role"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {!isConsultingCompany && ( // Only show client management for non-IBP companies
        <div className="mt-12">
          <h2 className="text-2xl font-bold mb-4">Clients</h2>

          {!canAddMoreClients() && !getFeatureAccess('enterprise') && (
            <Alert className="mb-6">
              <AlertDescription>
                You've reached the client limit for your plan ({getFeatureAccess('pro') ? 500 : 5} clients). <Link to="/dashboard/billing" className="text-primary font-medium">Upgrade to Enterprise</Link> for unlimited clients!
              </AlertDescription>
            </Alert>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <AddClientForm onSuccess={() => queryClient.invalidateQueries({ queryKey: ["clients", company?.id] })} />
            <InviteClientForm onSuccess={() => queryClient.invalidateQueries({ queryKey: ["invites"] })} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {clients?.map((client: any) => (
              <Card key={client.id}>
                <CardHeader className="flex flex-row items-center space-x-4 pb-2">
                  <div className="flex items-center space-x-2">
                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                      {client.name?.[0]?.toUpperCase()}
                    </div>
                    <div>
                      <h3 className="font-medium">{client.name}</h3>
                      <p className="text-sm text-muted-foreground">{client.email}</p>
                    </div>
                  </div>
                  <div className="ml-auto space-x-1">
                    <Badge variant={client.status === 'active' ? "default" : "secondary"}>{client.status}</Badge>
                    {isConsultingCompany && client.kyc_status && <Badge variant="outline">{client.kyc_status}</Badge>}
                  </div>
                </CardHeader>
                <CardContent>
                  {client.notes && <p className="text-sm text-muted-foreground mb-4">{client.notes}</p>}
                  {isConsultingCompany && !client.kyc_status && (
                    <Button variant="outline" size="sm" className="w-full" onClick={() => setSelectedClientId(client.id)}>
                      Complete KYC
                    </Button>
                  )}
                  {isConsultingCompany && client.kyc_status === 'pending' && (
                    <div className="flex space-x-2">
                      <Button variant="outline" size="sm" onClick={() => {/* Verify modal */}}>
                        Verify KYC
                      </Button>
                      <Button variant="destructive" size="sm">Reject</Button>
                    </div>
                  )}
                  {client.id_document_url && (
                    <a href={client.id_document_url} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline">
                      View ID Document
                    </a>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
          {clients?.length === 0 && (
            <Card>
              <CardContent className="text-center py-8">
                <Users className="h-12 w-12 text-muted-foreground mx-auto mb-2" />
                <p className="text-muted-foreground">No clients yet. Add one to get started!</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

export default Contacts;